import type { ArtifactItem } from '../../../common/components/ArtifactPanel';
import type { ViewModel } from '../Models/ViewModel';

function normalizeRoutePath(routePath?: string): string | null {
	if (!routePath?.trim()) {
		return null;
	}

	const pathWithoutQueryOrHash = routePath.trim().split(/[?#]/, 1)[0];
	const segments = pathWithoutQueryOrHash.split('/').filter(Boolean);
	return segments.length > 0 ? `/${segments.join('/')}` : '/';
}

/** Nests a view beneath the closest view whose route is its path-segment ancestor. */
export function createRouteGroupedViewItems(views: ViewModel[], createItem: (view: ViewModel) => ArtifactItem): ArtifactItem[] {
	const items = views.map((view) => ({ view, item: createItem(view), routePath: normalizeRoutePath(view.RoutePath) }));
	const itemsByRoute = new Map<string, typeof items>();
	for (const entry of items) {
		if (!entry.routePath) {
			continue;
		}
		itemsByRoute.set(entry.routePath, [...(itemsByRoute.get(entry.routePath) ?? []), entry]);
	}

	const parentByItemId = new Map<string, string>();
	for (const entry of items) {
		if (!entry.routePath) {
			continue;
		}

		const segments = entry.routePath.split('/').filter(Boolean);
		for (let depth = segments.length - 1; depth > 0; depth -= 1) {
			const parentPath = `/${segments.slice(0, depth).join('/')}`;
			const parents = itemsByRoute.get(parentPath);
			if (parents?.length === 1) {
				parentByItemId.set(entry.item.id, parents[0].item.id);
				break;
			}
		}
	}

	const childrenByParentId = new Map<string, ArtifactItem[]>();
	for (const entry of items) {
		const parentId = parentByItemId.get(entry.item.id);
		if (parentId) {
			childrenByParentId.set(parentId, [...(childrenByParentId.get(parentId) ?? []), entry.item]);
		}
	}

	const addRouteChildren = (item: ArtifactItem): ArtifactItem => {
		const children = childrenByParentId.get(item.id);
		return children?.length ? { ...item, children: children.map(addRouteChildren) } : item;
	};

	return items.filter((entry) => !parentByItemId.has(entry.item.id)).map((entry) => addRouteChildren(entry.item));
}
